import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getForm,
  addEventsForForm,
} from '@pingum/app-state-management/form/actions';
import {
  map,
  uniqBy,
  flatten,
  sortBy,
} from 'lodash';
import {
  makeStyles,
  Typography,
  LinearProgress,
  Button,
  CircularProgress,
  Grow,
} from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import ReCAPTCHA from 'react-google-recaptcha';

import defaultTheme from '../../../utils/Theme';
import * as brandColors from '../../../utils/brand-colors';
import { getEventDataValidationText } from '../../../utils/eventDataHelper';
import DisplayMessage from '../../widgets/DisplayMessage';
import EventTemplateForms from './components/EventTemplateForms';
import styles from './styles';

const useStyles = makeStyles(styles);

const getFormEventDataIds = (formEventDataIds) => (!formEventDataIds
  ? []
  : formEventDataIds);
const getHiddenEventDataIds = (hiddenEventDataIds) => (!hiddenEventDataIds
  ? []
  : hiddenEventDataIds);

const getEventDataWithAddedFields = (eventData, defaultValues) => (eventData.map((ed) => ({
  ...ed,
  error: false,
  value: defaultValues[ed.id] || '',
  helperText: '',
})));

const getEventDataRequestFromArray = (eventData) => {
  const request = {};
  for (let i = 0; i < eventData.length; i += 1) {
    request[eventData[i].name] = {
      type: eventData[i].type,
      value: eventData[i].value
        ? eventData[i].value
        : '',
    };
  }
  return request;
};

const checkAndAddHiddenFieldsWithDefaultValues = (hiddenEventDataIds, eventTemplate) => {
  const hiddenValuesToAdd = {};
  // loop over hidden event data ids for this form and look to see if the workflow has the field associated
  // and add that hidden field as event data if there is a default value
  if (hiddenEventDataIds && hiddenEventDataIds.length > 0) {
    for (let i = 0; i < hiddenEventDataIds.length; i += 1) {
      if (eventTemplate.eventDataDefaultValues[hiddenEventDataIds[i]]) {
        const foundEventData = eventTemplate.eventData.filter((ed) => parseInt(ed.id, 10) === parseInt(hiddenEventDataIds[i], 10));
        if (foundEventData.length === 1) {
          hiddenValuesToAdd[foundEventData[0].name] = {
            type: foundEventData[0].type,
            value: eventTemplate.eventDataDefaultValues[foundEventData[0].id],
          };
        }
      }
    }
  }
  return hiddenValuesToAdd;
};

export const FormPageComponent = () => {
  const { id } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();

  // local state
  const [formTheme, setFormTheme] = useState(defaultTheme);
  const [formSuccess, setFormSuccess] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formEventTemplateError, setFormEventTemplateError] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(brandColors.primary[500]);
  const [formEventData, setFormEventData] = useState({ value: [] });
  const [formEventTemplates, setFormEventTemplates] = useState({ value: [] });
  const [eventTemplates, setEventTemplates] = useState([]);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);

  const recaptchaRef = useRef();

  // redux state
  const {
    isFetchingForm,
    form,
    isAddingFormEvents,
  } = useSelector((state) => state.form);

  const handleSelectEventTemplate = (eventTemplate) => {
    const formEventDataIds = getFormEventDataIds(form.formEventDataIds);
    const hiddenEventDataIds = getHiddenEventDataIds(form.hiddenEventDataIds);
    const formEventTemplatesToKeep = (form.type === 'Multiple'
      ? formEventTemplates.value
      : []);
    const isAddingTemplate = formEventTemplatesToKeep.map((et) => et.id).indexOf(eventTemplate.id) < 0;

    if (isAddingTemplate) {
      setFormEventTemplates({
        value: [...formEventTemplatesToKeep, {
          ...eventTemplate,
          eventData: sortBy(getEventDataWithAddedFields(eventTemplate.eventData
            .filter((ed) => formEventDataIds.indexOf(ed.id) < 0)
            .filter((ed) => hiddenEventDataIds.indexOf(ed.id) < 0), eventTemplate.eventDataDefaultValues), ['name']),
        }],
      });
    } else {
      setFormEventTemplates({ value: formEventTemplatesToKeep.filter((et) => et.id !== eventTemplate.id) });
    }
  };

  const resetForm = (formId) => {
    setFormTheme(defaultTheme);
    setFormSuccess(null);
    setFormError(null);
    setFormEventTemplateError(null);
    setFormEventData({ value: [] });
    setFormEventTemplates({ value: [] });
    setEventTemplates([]);
    if (!!formId && formId.length > 0) {
      dispatch(getForm(formId))
        .catch((error) => {
          setFormError(error
            ? error.data
            : {
              error: 'Server Error',
              message: 'There was an error loading this form, please refresh the page to try again. If the issue persists, contact support@pingum.io',
            });
        });
    }
  };

  useEffect(() => {
    resetForm(id);
  }, [id]);

  useEffect(() => {
    if (!!form && !!form.id) {
      if (form.archived) {
        setFormError({
          error: 'Archived Form',
          message: 'This form is no longer active. If you think this is a mistake, contact the owner of the form.',
        });
        return;
      }

      if (form.primaryColor && form.primaryColor.length > 0) {
        const { palette } = defaultTheme;
        const { primary } = palette;
        setFormTheme({
          ...defaultTheme,
          palette: {
            ...palette,
            primary: {
              ...primary,
              main: form.primaryColor,
            },
          },
        });
        setPrimaryColor(form.primaryColor);
      }

      setEventTemplates(sortBy(form.eventTemplates, ['name']).map((et) => ({
        ...et,
        eventData: getEventDataWithAddedFields(et.eventData, et.eventDataDefaultValues),
      })));

      const allDefaultValues = [
        ...form.eventTemplates.map((et) => et.eventDataDefaultValues),
      ];
      const allEventData = getEventDataWithAddedFields(map(uniqBy(flatten(form.eventTemplates.map((et) => et.eventData)), 'id'), (item) => item), allDefaultValues);
      setFormEventData({ value: sortBy(allEventData.filter((ed) => getFormEventDataIds(form.formEventDataIds).indexOf(ed.id) >= 0), ['name']) });

      if (form.type === 'Single' && form.eventTemplates.length === 1) {
        handleSelectEventTemplate(form.eventTemplates[0]);
      }
    }
  }, [form]);

  const checkEventDataValid = () => {
    const updatedFormEventData = formEventData.value;
    let isValid = true;
    for (let i = 0; i < updatedFormEventData.length; i += 1) {
      const isValidResponse = getEventDataValidationText(updatedFormEventData[i], updatedFormEventData[i].required);
      updatedFormEventData[i].error = !!isValidResponse;
      updatedFormEventData[i].helperText = isValidResponse;
      if (isValidResponse) {
        isValid = false;
      }
    }
    setFormEventData({ value: updatedFormEventData });

    return isValid;
  };

  const checkEventTemplateEventDataValid = () => {
    let isValid = true;
    const updatedFormEventTemplates = formEventTemplates.value;

    if (updatedFormEventTemplates.length === 0) {
      isValid = false;
      setFormEventTemplateError('Please select a form');
    } else {
      setFormEventTemplateError(null);
    }

    for (let i = 0; i < updatedFormEventTemplates.length; i += 1) {
      for (let j = 0; j < updatedFormEventTemplates[i].eventData.length; j += 1) {
        const isValidResponse
          = getEventDataValidationText(updatedFormEventTemplates[i].eventData[j], updatedFormEventTemplates[i].eventData[j].required);
        updatedFormEventTemplates[i].eventData[j].error = !!isValidResponse;
        updatedFormEventTemplates[i].eventData[j].helperText = isValidResponse;
        if (isValidResponse) {
          isValid = false;
        }
      }
    }
    setFormEventTemplates({ value: updatedFormEventTemplates });

    return isValid;
  };

  const handleSubmit = () => {
    // are form level event data fields valid?
    const formEventDataValid = checkEventDataValid();
    // are each selected event workflow event data fields valid?
    const eventTemplateDataValid = checkEventTemplateEventDataValid();

    if (formEventDataValid && eventTemplateDataValid && recaptchaVerified) {
      const request = { createEventFromTemplates: {} };

      // get the event data object for the form level fields
      const formEventDataRequest = getEventDataRequestFromArray(formEventData.value);

      // loop over each selected event workflow in the form and add the cooresponding event data
      // ie not the form level fields and any hidden fields that have a default value
      for (let index = 0; index < formEventTemplates.value.length; index += 1) {
        const originalEventTemplateObject = form.eventTemplates.filter((ed) => ed.id === formEventTemplates.value[index].id);
        request.createEventFromTemplates[formEventTemplates.value[index].id] = {
          eventData: {
          // form level fields
            ...formEventDataRequest,
            // workflow specific fields that are NOT hidden
            ...getEventDataRequestFromArray(formEventTemplates.value[index].eventData),
            // any hidden fields associated with workflow that has default value
            ...checkAndAddHiddenFieldsWithDefaultValues(form.hiddenEventDataIds, originalEventTemplateObject.length === 1
              ? originalEventTemplateObject[0]
              : formEventTemplates.value[index]),
          },
        };
      }

      dispatch(addEventsForForm(id, request)).then(() => {
        setFormSuccess('Form Successfully Submitted');
      }).catch((error) => {
        setFormError(error
          ? error.data
          : {
            error: 'Server Error',
            message: 'There was an error submitting this form, please refresh the page to try again. If the issue persists, contact support@pingum.io',
          });
      });
    }
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaVerified(!!value);
  };

  const submitLabel = (!!form.submitLabel && form.submitLabel.length > 0)
    ? form.submitLabel
    : 'Press the submit button to complete the form.';
  const submitButtonText = (!!form.submitButtonText && form.submitButtonText.length > 0)
    ? form.submitButtonText
    : 'Submit';

  const getDisplayMessage = () => (
    formSuccess
      ? (
        <Grow
          in={!!formSuccess}
          timeout={1500}
        >
          <div>
            <DisplayMessage
              title={formSuccess}
              subTitle={<div><Typography component="div">Would you like to submit another form?</Typography> <Button onClick={() => { resetForm(id); }}>Click Here</Button></div>}
              icon="check_outline"
              color={primaryColor}
              iconColor="white"
            />
          </div>
        </Grow>
      )
      : (
        <DisplayMessage
          title={formError.error}
          subTitle={formError.message}
          icon="error_outline"
        />
      )
  );

  return (
    <MuiThemeProvider theme={formTheme}>
      <div className={classes.container}>
        {
          (formError || formSuccess)
            ? (
              <div className={classes.content}>{getDisplayMessage()}</div>
            )
            : (
              <div className={classes.content}>
                {
                (isFetchingForm)
                  ? (
                    <div className={classes.loadingContainer}>
                      <Typography variant="caption" component="div" gutterBottom>Loading form...</Typography>
                      <LinearProgress />
                    </div>
                  )
                  : (
                    <div>
                      <Typography variant="h2" gutterBottom align="center">{form.title}</Typography>
                      {
                        (form.description && form.description.length > 0)
                        && <Typography variant="subtitle1" component="div" align="center" gutterBottom>{form.description}</Typography>
                      }

                      <EventTemplateForms
                        formEventData={formEventData}
                        handleSetFormEventData={setFormEventData}
                        eventTemplates={eventTemplates}
                        handleSelectEventTemplate={handleSelectEventTemplate}
                        formEventTemplates={formEventTemplates}
                        handleSetFormEventTemplates={setFormEventTemplates}
                        disabled={isFetchingForm || isAddingFormEvents}
                        formType={form.type}
                        formEventTemplateError={formEventTemplateError}
                      />

                      <div className={classes.recaptcha}>
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={env.REACT_APP_RECAPTCHA_KEY}
                          onChange={handleRecaptchaChange}
                        />
                      </div>
                      <div className={classes.submitContainer}>
                        <Typography variant="caption" gutterBottom align="center" component="div">{submitLabel}</Typography>
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={isFetchingForm || isAddingFormEvents || !recaptchaVerified}
                        >
                          {
                            (isFetchingForm || isAddingFormEvents)
                              && <CircularProgress size={20} className={classes.loadingButtonProgress} color="inherit" />
                          }
                          {submitButtonText}
                        </Button>
                      </div>
                    </div>
                  )
              }
              </div>
            )
        }
      </div>
    </MuiThemeProvider>
  );
};

FormPageComponent.propTypes = { };
FormPageComponent.defaultProps = { };

export default FormPageComponent;
